import { useAuth0 } from "@auth0/auth0-react";
import SplineApi from "../../tools/spline-api.js";
import * as Parse from "papaparse";
import useGeneralUsTreasuryStore from "../../stores/general-us-treasury.store.ts";
import moment, { Moment } from "moment";

let inFlightUSTRequest: Promise<[Moment, number[]][]> | null = null;

export default function useUsTreasury() {
  const { getAccessTokenSilently } = useAuth0();
  const { setUsTreasury, usTreasury } = useGeneralUsTreasuryStore();

  /**
   * Fetches the US Treasury data.
   */
  async function fetchUsTreasury(): Promise<[Moment, number[]][]> {
    const token = await getAccessTokenSilently();
    const api = new SplineApi(token);
    const csv = await api.getTreasuryData()
    const csvRows = Parse.parse(csv).data.filter(x => x[0] !== "");

    const formattedRows: [Moment, number[]][] = [];
    for (let i = 1; i < csvRows.length; i++) {
      const row = csvRows[i];
      const formattedRow = formatCSVRow(row);
      formattedRows.push(formattedRow)
    }

    return formattedRows;
  }
  
  /**
   * Returns the US Treasy data.
   * 
   * @remarkss It only fetched the data once and caches the result.
   */
  async function getUsTreasury(): Promise<[Moment, number[]][]> {
    if (usTreasury.length !== 0) return usTreasury;

    if (!inFlightUSTRequest) {
      inFlightUSTRequest = fetchUsTreasury().then((newAAA) => {
        setUsTreasury(newAAA);
        inFlightUSTRequest = null;
        return newAAA;
      }).catch((err) => {
        inFlightUSTRequest = null;
        throw err;
      });
    }

    return inFlightUSTRequest;
  }

  /**
   * Returns the US Treasury aligned with the market conditions date
   */
  async function getAlignedUSTreasury(
    marketConditionsDate: Moment,
    principalDates: string[]
  ): Promise<[number[], any]> {
    const usTreasury = await getUsTreasury();
    const date = usTreasury[usTreasury.length - 1][0].clone();
    const usTreasuryTenors = usTreasury[usTreasury.length - 1][1];
    const yields: number[] = [];

    for (const dateStr of principalDates) {
      const maturityDate = moment(dateStr);
      const yearsToMaturity = Math.round(
        maturityDate.diff(marketConditionsDate, "years", true)
      );

      const boundedYearsToMaturity = Math.max(1, yearsToMaturity)
      yields.push(usTreasuryTenors[boundedYearsToMaturity - 1]);
    }
    
    const title = "\"UST\"";
    const description = "US Treasury " + date.format("MM/DD/YYYY") + " (interpolated).";
    return [yields, { title, description }]
  }

  /**
   * Converts one row of CSV data into usable tenors.
   */
  function formatCSVRow(row: any[]): [Moment, number[]] {
    const date = moment(row[0], "YYYY-MM-DD");
    const values = interpolateValues(row);
    return [date, values];
  }

  /**
   * Given a CSV row, interpolates all 30 tenors.
   */
  type YearValue = { year: number; value: number };
  function interpolateValues(row: string[]): number[] {
    const known: YearValue[] = [
      { year: 1, value: parseFloat(row[4]) / 100 },
      { year: 2, value: parseFloat(row[5]) / 100 },
      { year: 3, value: parseFloat(row[6]) / 100 },
      { year: 5, value: parseFloat(row[7]) / 100 },
      { year: 7, value: parseFloat(row[8]) / 100 },
      { year: 10, value: parseFloat(row[9]) / 100 },
      { year: 20, value: parseFloat(row[10]) / 100 },
      { year: 30, value: parseFloat(row[11]) / 100 },
    ];

    const result: number[] = [];

    for (let year = 1; year <= 30; year++) {
      const exact = known.find(k => k.year === year);
      if (exact) {
        result.push(exact.value);
      } else {
        const lower = [...known].reverse().find(k => k.year < year);
        const upper = known.find(k => k.year > year);
        if (lower && upper) {
          const ratio = (year - lower.year) / (upper.year - lower.year);
          const interpolated = lower.value + ratio * (upper.value - lower.value);
          result.push(interpolated);
        } else {
          result.push(NaN); // should not happen
        }
      }
    }

    return result;
  }

  async function getDateRates(date: Moment): Promise<number[] | undefined> {
    const realTimeAAA = await getUsTreasury();
    const entry = realTimeAAA.find(x => x[0].isSame(date, "day"));
    return entry?.[1];
  }

  async function getUSTRateForSavedDeal(
    bidDate: Moment,
    yearsToMaturity: number
  ): Promise<number | undefined> {
    const rates = await getDateRates(bidDate);
    return rates?.[yearsToMaturity - 1];
  }

  return { getUsTreasury, getAlignedUSTreasury, getUSTRateForSavedDeal };
}
